import { io, type Socket } from 'socket.io-client'
import { commandError, type Ack, type ClientToServerEvents, type ServerToClientEvents } from '@chess-uno/shared-protocol'
import { SERVER_URL, type StoredSession } from './session.js'

export type ClientSocket = Socket<ServerToClientEvents, ClientToServerEvents>

let socket: ClientSocket | null = null
let socketToken: string | null = null

/** Single Socket.IO connection per session (automatic reconnection). */
export function getSocket(session: StoredSession): ClientSocket {
  if (socket && socketToken === session.token) return socket
  socket?.disconnect()
  socket = io(SERVER_URL, {
    auth: { token: session.token },
    transports: ['websocket', 'polling'],
    reconnection: true,
    reconnectionDelay: 500,
    reconnectionDelayMax: 5_000,
  })
  socketToken = session.token
  return socket
}

export function disconnectSocket(): void {
  socket?.disconnect()
  socket = null
  socketToken = null
}

type Payload<K extends keyof ClientToServerEvents> = Parameters<ClientToServerEvents[K]>[0]
type Reply<K extends keyof ClientToServerEvents> = Parameters<Parameters<ClientToServerEvents[K]>[1]>[0]

/** Emit with a typed acknowledgement and a timeout. */
export function request<K extends keyof ClientToServerEvents>(
  s: ClientSocket,
  event: K,
  payload: Payload<K>,
  timeoutMs = 10_000,
): Promise<Reply<K>> {
  return new Promise((resolve) => {
    const timer = setTimeout(() => {
      resolve({ ok: false, error: commandError('internal', 'server_unreachable') } as Reply<K>)
    }, timeoutMs)
    ;(s.emit as unknown as (e: K, p: Payload<K>, cb: (r: Reply<K>) => void) => void)(event, payload, (r) => {
      clearTimeout(timer)
      resolve(r)
    })
  })
}

export type { Ack }
